import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'
import { CustomTemplatesDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  CreateCustomTemplateFromPredefinedDocument,
  ReviewProcessPredefinedTemplateDocument,
} from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { TemplatePreview } from '../TemplatePreview/TemplatePreview'
import { PredefinedTemplate } from '../types'

type Props = {
  template: PredefinedTemplate
  canDuplicate: boolean
}
export const Actions: React.FC<Props> = ({ template, canDuplicate }) => {
  const { access, company } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const [previewSidepanelOpen, togglePreviewSidepanelOpen] = useToggle()

  const [createCustomTemplateFromPredefined] = useMutation(
    CreateCustomTemplateFromPredefinedDocument,
    {
      extractorFn: (data) =>
        data.performanceMutations
          .createFromPredefinedTemplatePerformanceReviewProcessCustomTemplate,
    }
  )

  const handleDuplicateTemplate = async () => {
    const response = await createCustomTemplateFromPredefined({
      awaitRefetchQueries: true,
      variables: {
        authorId: access.id,
        companyId: company.id,
        predefinedTemplateId: template.id,
      },
      refetchQueries: [CustomTemplatesDataTableDocument],
    })

    const handled = handleResult(response, () => undefined)

    if (handled.success) {
      const templateId = handled?.data?.reviewProcessCustomTemplate?.id
      app.setFlash({
        type: 'correct',
        message: i18n.t('performance_process.reviews.duplicate_review.modal.success_message'),
      })

      if (templateId) {
        navigate(
          navigation.performanceProcess.reviews.templates.template.fullPath({
            templateId: templateId,
          })
        )
      }
    }
  }

  return (
    <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
      {canDuplicate && (
        <Box flexDirection="row" justifyContent="flexEnd" alignItems="center" gap="s8">
          <PopoverMenu type="list">
            <PopoverMenu.Trigger>
              <Button.Decorative type="secondary" iconName="ellipsis" />
            </PopoverMenu.Trigger>
            <PopoverMenu.Content side="bottom" align="start">
              <PopoverMenu.TouchableOption
                title={i18n.t('performance_process.reviews_templates.duplicate_template.duplicate')}
                onClick={handleDuplicateTemplate}
              />
            </PopoverMenu.Content>
          </PopoverMenu>
        </Box>
      )}

      <Button
        type="secondary"
        onClick={togglePreviewSidepanelOpen(true)}
        iconName="eye-visible"
        aria-label={i18n.t('performance_process.reviews.templates.template_preview')}
      />

      {previewSidepanelOpen && (
        <Sidepanel
          open
          onClose={togglePreviewSidepanelOpen(false)}
          width="wideXL"
          title={i18n.t('performance_process.reviews.templates.template_preview')}
        >
          <TemplatePreview template={template} query={ReviewProcessPredefinedTemplateDocument} />
        </Sidepanel>
      )}
    </Box>
  )
}
